<script setup lang="ts">
import HomeLayout from "~/layouts/HomeLayout.vue"
import { CONSENT_POLICY_VERSION } from "~/composables/useConsent"

useHead({
    title: "Política de cookies",
})

interface PolicyCookie {
    name: string
    provider: string
    purpose_key: string
    duration_key: string
    type: string
}

interface PolicyCategory {
    key: "necessary" | "functional" | "analytics" | "marketing"
    cookies: PolicyCookie[]
}

const categories: PolicyCategory[] = [
    {
        key: "necessary",
        cookies: [
            {
                name: "dapamas_consent",
                provider: "Dapamas",
                purpose_key: "consent.cookies.dapamas_consent.purpose",
                duration_key: "consent.duration.year_1",
                type: "HTTP",
            },
            {
                name: "XSRF-TOKEN",
                provider: "Dapamas",
                purpose_key: "consent.cookies.xsrf.purpose",
                duration_key: "consent.duration.session",
                type: "HTTP",
            },
            {
                name: "dapamas_session",
                provider: "Dapamas",
                purpose_key: "consent.cookies.session.purpose",
                duration_key: "consent.duration.session",
                type: "HTTP",
            },
            {
                name: "selected-province",
                provider: "Dapamas",
                purpose_key: "consent.cookies.province.purpose",
                duration_key: "consent.duration.days_30",
                type: "HTTP",
            },
        ],
    },
    {
        key: "functional",
        cookies: [
            {
                name: "shops",
                provider: "Dapamas",
                purpose_key: "consent.cookies.shops.purpose",
                duration_key: "consent.duration.persistent",
                type: "localStorage",
            },
        ],
    },
    {
        key: "analytics",
        cookies: [
            {
                name: "_ga",
                provider: "Google LLC",
                purpose_key: "consent.cookies.ga.purpose",
                duration_key: "consent.duration.years_2",
                type: "HTTP",
            },
            {
                name: "_ga_*",
                provider: "Google LLC",
                purpose_key: "consent.cookies.ga_session.purpose",
                duration_key: "consent.duration.years_2",
                type: "HTTP",
            },
            {
                name: "_gid",
                provider: "Google LLC",
                purpose_key: "consent.cookies.gid.purpose",
                duration_key: "consent.duration.hours_24",
                type: "HTTP",
            },
        ],
    },
    {
        key: "marketing",
        cookies: [
            {
                name: "_gcl_au",
                provider: "Google LLC",
                purpose_key: "consent.cookies.gcl.purpose",
                duration_key: "consent.duration.months_3",
                type: "HTTP",
            },
            {
                name: "IDE",
                provider: "Google LLC (DoubleClick)",
                purpose_key: "consent.cookies.ide.purpose",
                duration_key: "consent.duration.year_1",
                type: "HTTP",
            },
        ],
    },
]

const lastUpdated = "2026-06-04"
</script>

<template>
    <HomeLayout>
        <div class="tw-max-w-4xl tw-mx-auto tw-px-4 tw-py-10">
            <p class="tw-text-3xl tw-font-bold tw-mb-2">
                {{ $t("consent.policy.title") }}
            </p>
            <p class="tw-text-sm tw-text-gray-500 tw-mb-8">
                {{
                    $t("consent.policy.version", {
                        version: CONSENT_POLICY_VERSION,
                    })
                }}
                &middot;
                {{ $t("consent.policy.last_updated", { date: lastUpdated }) }}
            </p>

            <section class="tw-prose tw-max-w-none">
                <p>{{ $t("consent.policy.what_are_cookies.title") }}</p>
                <p>{{ $t("consent.policy.what_are_cookies.body") }}</p>

                <p>{{ $t("consent.policy.controller.title") }}</p>
                <p>{{ $t("consent.policy.controller.body") }}</p>

                <p>{{ $t("consent.policy.categories.title") }}</p>

                <div v-for="cat in categories" :key="cat.key" class="tw-mb-8">
                    <p>{{ $t(`consent.categories.${cat.key}.label`) }}</p>
                    <p>{{ $t(`consent.categories.${cat.key}.description`) }}</p>
                    <div class="tw-overflow-x-auto">
                        <table class="policy-table">
                            <thead>
                                <tr>
                                    <th>{{ $t("consent.table.name") }}</th>
                                    <th>{{ $t("consent.table.provider") }}</th>
                                    <th>{{ $t("consent.table.purpose") }}</th>
                                    <th>{{ $t("consent.table.duration") }}</th>
                                    <th>{{ $t("consent.table.type") }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="ck in cat.cookies" :key="ck.name">
                                    <td class="tw-font-mono tw-text-xs">
                                        {{ ck.name }}
                                    </td>
                                    <td>{{ ck.provider }}</td>
                                    <td>{{ $t(ck.purpose_key) }}</td>
                                    <td>{{ $t(ck.duration_key) }}</td>
                                    <td>{{ ck.type }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <p>{{ $t("consent.policy.third_parties.title") }}</p>
                <p>{{ $t("consent.policy.third_parties.body") }}</p>
                <ul>
                    <li>
                        <a
                            href="https://policies.google.com/privacy"
                            target="_blank"
                            rel="noopener"
                        >
                            Google — Privacy Policy
                        </a>
                    </li>
                    <li>
                        <a
                            href="https://business.safety.google/adscontrols/"
                            target="_blank"
                            rel="noopener"
                        >
                            Google Ads Controls
                        </a>
                    </li>
                </ul>

                <p>{{ $t("consent.policy.rights.title") }}</p>
                <p>{{ $t("consent.policy.rights.body") }}</p>

                <p>{{ $t("consent.policy.manage.title") }}</p>
                <p>{{ $t("consent.policy.manage.body") }}</p>
                <p>
                    <ConsentReopenLink />
                </p>

                <p>{{ $t("consent.policy.contact.title") }}</p>
                <p>{{ $t("consent.policy.contact.body") }}</p>
            </section>
        </div>
    </HomeLayout>
</template>

<style scoped>
.policy-table {
    width: 100%;
    font-size: 13px;
    border-collapse: collapse;
}
.policy-table th,
.policy-table td {
    padding: 8px 10px;
    border-bottom: 1px solid #e5e7eb;
    text-align: left;
    vertical-align: top;
}
.policy-table th {
    background-color: #f3f4f6;
    font-weight: 600;
}
</style>
